// src/Dashboard/pages/user/PassageCeintureDocuments/hooks/usePassageCeintureDocuments.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllPassageCeintureDocuments,
  getPublishedPassageCeintureDocuments,
  deletePassageCeintureDocument,
} from "../../../../../services/passageCeintureDocumentsServices";

import { getBeltsList } from "../../../../../services/beltServices";

import {
  DEFAULT_UI_STATE,
  beltRank,
  getCurrentUserRoleId,
  getDocumentSearchText,
  getDocumentStatus,
  normalizeBeltLabel,
} from "../utils/passageCeintureDocumentsUtils";

const STORAGE_KEY = "passage_ceinture_documents_dashboard_ui";

const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

export function usePassageCeintureDocuments() {
  const roleId = getCurrentUserRoleId();
  const canManage = [1, 3].includes(Number(roleId));

  const [documents, setDocuments] = useState([]);
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [ui, setUi] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return saved ? { ...DEFAULT_UI_STATE, ...saved } : DEFAULT_UI_STATE;
    } catch {
      return DEFAULT_UI_STATE;
    }
  });

  const [debouncedSearch, setDebouncedSearch] = useState(ui.search || "");

  const [openedDocument, setOpenedDocument] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [drawerMode, setDrawerMode] = useState("read"); // read | create | edit

  const persistUi = useCallback((next) => {
    setUi(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const updateUi = useCallback(
    (patch) => {
      persistUi({ ...ui, ...patch });
    },
    [persistUi, ui]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      persistUi({ ...ui, search: debouncedSearch, perPage: DEFAULT_UI_STATE.perPage });
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedSearch]); // volontairement limité

  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);

      const data = canManage
        ? await getAllPassageCeintureDocuments()
        : await getPublishedPassageCeintureDocuments();

      setDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement documents passage ceinture :", error);
      toast.error("Erreur lors du chargement des documents.");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  const loadBelts = useCallback(async () => {
    try {
      const data = await getBeltsList();
      setBelts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement ceintures :", error);
      setBelts([]);
    }
  }, []);

  const refresh = useCallback(async () => {
    await Promise.all([loadDocuments(), loadBelts()]);
  }, [loadDocuments, loadBelts]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const beltOptions = useMemo(() => {
    const fromBelts = belts
      .map((belt) => normalizeBeltLabel(belt))
      .filter(Boolean);

    const fromDocs = documents
      .map((document) => document?.ceinture)
      .filter(Boolean);

    const unique = Array.from(new Set([...fromBelts, ...fromDocs]));

    return unique.sort((a, b) => {
      const rank = beltRank(a) - beltRank(b);
      if (rank !== 0) return rank;
      return collatorFR.compare(String(a), String(b));
    });
  }, [belts, documents]);

  const filteredDocuments = useMemo(() => {
    const search = String(ui.search || "").trim().toLowerCase();

    const list = documents.filter((document) => {
      const status = getDocumentStatus(document);

      const matchSearch =
        !search || getDocumentSearchText(document).includes(search);

      const matchBelt =
        !ui.belt ||
        ui.belt === "all" ||
        String(document?.ceinture || "") === String(ui.belt);

      const matchPublication = canManage
        ? ui.showDrafts
          ? true
          : status === "published"
        : status === "published";

      return matchSearch && matchBelt && matchPublication;
    });

    list.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""));
      }

      if (ui.sort === "belt") {
        const rank = beltRank(a?.ceinture) - beltRank(b?.ceinture);
        if (rank !== 0) return rank;

        return collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""));
      }

      const rank = beltRank(a?.ceinture) - beltRank(b?.ceinture);
      if (rank !== 0) return rank;

      const order =
        Number(a?.ordre_affichage || 0) - Number(b?.ordre_affichage || 0);

      if (order !== 0) return order;

      return collatorFR.compare(String(a?.titre || ""), String(b?.titre || ""));
    });

    return list;
  }, [documents, ui, canManage]);

  const visibleDocuments = useMemo(() => {
    return filteredDocuments.slice(0, ui.perPage);
  }, [filteredDocuments, ui.perPage]);

  const stats = useMemo(() => {
    const total = documents.length;
    const published = documents.filter((doc) => doc?.is_published).length;
    const drafts = total - published;
    const beltsCount = new Set(documents.map((doc) => doc?.ceinture).filter(Boolean)).size;
    const withPdf = documents.filter((doc) => !!doc?.pdf_url).length;

    return {
      total,
      published,
      drafts,
      belts: beltsCount,
      withPdf,
    };
  }, [documents]);

  const resetFilters = useCallback(() => {
    setDebouncedSearch("");
    persistUi(DEFAULT_UI_STATE);
  }, [persistUi]);

  const loadMore = useCallback(() => {
    persistUi({
      ...ui,
      perPage: ui.perPage + DEFAULT_UI_STATE.perPage,
    });
  }, [persistUi, ui]);

  const openCreateDrawer = useCallback(() => {
    setOpenedDocument(null);
    setEditingDocument(null);
    setDrawerMode("create");
  }, []);

  const openEditDrawer = useCallback((document) => {
    setOpenedDocument(null);
    setEditingDocument(document);
    setDrawerMode("edit");
  }, []);

  const openReadDrawer = useCallback((document) => {
    setEditingDocument(null);
    setOpenedDocument(document);
    setDrawerMode("read");
  }, []);

  const closeDrawer = useCallback(() => {
    setOpenedDocument(null);
    setEditingDocument(null);
    setDrawerMode("read");
  }, []);

  const handleDelete = useCallback(
    async (document) => {
      if (!canManage || !document?.id) return;

      const ok = window.confirm(
        `Supprimer le document "${document?.titre || "Sans titre"}" ?`
      );

      if (!ok) return;

      try {
        await deletePassageCeintureDocument(document.id);
        toast.success("Document supprimé avec succès.");
        await loadDocuments();
      } catch (error) {
        console.error("Erreur suppression document :", error);
        toast.error("Erreur lors de la suppression du document.");
      }
    },
    [canManage, loadDocuments]
  );

  const handleSaved = useCallback(async () => {
    await loadDocuments();
    closeDrawer();
  }, [loadDocuments, closeDrawer]);

  return {
    loading,
    documents,
    filteredDocuments,
    visibleDocuments,
    belts: beltOptions,
    canManage,

    ui,
    debouncedSearch,
    setDebouncedSearch,
    updateUi,
    resetFilters,
    loadMore,

    stats,

    openedDocument,
    setOpenedDocument,
    editingDocument,
    setEditingDocument,
    drawerMode,
    setDrawerMode,

    openCreateDrawer,
    openEditDrawer,
    openReadDrawer,
    closeDrawer,

    handleDelete,
    handleSaved,
    refresh,
  };
}